import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, Check, GripHorizontal, Megaphone, Swords, UserPlus, Wallet, X } from "lucide-react";
import { friendsStore, shortAddr, useFriends } from "../store/friendsStore";
import { avatarUrl, type MatchCheckIn } from "../auth/api";
import { GAME_BY_KEY } from "../lib/games";
import { useUsdcBalance, formatUSDC, parseUSDC } from "../hooks/useGames";
import { SOLANA_NETWORK, LAMPORTS_PER_SOL, solToLamports } from "../contracts/solana/config";
import { useSolanaMoney } from "../hooks/useSolanaMoney";
import { useSolUsdPrice } from "../hooks/useSolUsdPrice";
import Avatar from "./Avatar";

interface NotificationsPanelProps {
  onClose: () => void;
  /** Route the user into the private match they just accepted. */
  onAcceptInvite?: (checkIn: MatchCheckIn) => void;
  /** Accepter's wallet — gates accepting a challenge on having the stake. */
  walletAddress?: string | null;
  /** Open the wallet panel so the user can top up before accepting. */
  onShowWallet?: () => void;
}

const PANEL_WIDTH = 380;

function defaultPosition() {
  if (typeof window === "undefined") return { x: 0, y: 80 };
  return { x: Math.max(16, window.innerWidth - PANEL_WIDTH - 24), y: 80 };
}

export default function NotificationsPanel({
  onClose,
  onAcceptInvite,
  walletAddress,
  onShowWallet,
}: NotificationsPanelProps) {
  const [pos, setPos] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const friends = useFriends();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      setPos({
        x: Math.max(-PANEL_WIDTH + 80, Math.min(newX, window.innerWidth - 80)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 60)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  const startDrag = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-no-drag]")) return;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  };

  const incoming = friends.incoming;
  const invites = friends.matchInvitesIn;
  const staff = friends.staffMessages;
  const unreadStaff = staff.filter((m) => !m.readAt).length;
  const totalUnread = incoming.length + invites.length + unreadStaff;
  const isEmpty = incoming.length === 0 && invites.length === 0 && staff.length === 0;

  // Accepter-side balance gate: like the sender's challenge modal, we verify
  // the wallet can cover the stake before letting them accept — otherwise both
  // players enter a match that aborts at the escrow lock. Poll while open.
  // EVM read nulled on Solana (unconfigured USDC reads 0 and would block);
  // on Solana the settler locks from the Vault `available`, so gate on that.
  const balance = useUsdcBalance(SOLANA_NETWORK ? null : walletAddress ?? null, 15_000);
  const sol = useSolanaMoney(SOLANA_NETWORK ? walletAddress ?? null : null);
  const { price: solPrice } = useSolUsdPrice();
  const balanceUsd = SOLANA_NETWORK
    ? sol.available != null && solPrice != null && solPrice > 0
      ? (Number(sol.available) / LAMPORTS_PER_SOL) * solPrice
      : 0
    : Number(formatUSDC(balance.value));
  /** True only when we have a confident reading below the stake (not on
   *  load / RPC error, so a transient read can't wrongly block accepting). */
  const cantAfford = (stake: number) =>
    SOLANA_NETWORK
      ? sol.available != null && solPrice != null && solPrice > 0 && stake > 0 &&
        sol.available < solToLamports(stake / solPrice)
      : !balance.loading && !balance.error && balance.value < parseUSDC(stake);

  const handleAcceptInvite = async (id: string) => {
    const checkIn = await friendsStore.acceptInvite(id);
    if (checkIn) {
      onAcceptInvite?.(checkIn);
      onClose();
    }
  };

  const openWallet = () => {
    onShowWallet?.();
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      className="fixed z-50 bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
      style={{
        left: pos.x,
        top: pos.y,
        width: PANEL_WIDTH,
        userSelect: dragging ? "none" : "auto",
      }}
    >
      <div className="h-1 bg-gradient-to-r from-[#FF6B00] to-[#F54D12]" />

      <div
        onMouseDown={startDrag}
        className="px-5 py-3.5 flex items-center justify-between border-b border-white/5"
        style={{ cursor: dragging ? "grabbing" : "grab" }}
      >
        <div className="flex items-center gap-2.5">
          <GripHorizontal className="w-4 h-4 text-white/30" aria-hidden="true" />
          <Bell className="w-4 h-4 text-white/70" />
          <span className="text-[13px] text-white font-semibold tracking-tight">Notifications</span>
          {totalUnread > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-[#FF6B00] text-black font-mono text-[10px] font-bold tabular-nums">
              {totalUnread}
            </span>
          )}
        </div>
        <button
          data-no-drag
          onClick={onClose}
          aria-label="Close"
          className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="max-h-[460px] overflow-auto">
        {isEmpty ? (
          <div className="py-16 px-6 flex flex-col items-center text-center">
            <BellOff className="w-10 h-10 text-white/20 mb-3" strokeWidth={1.5} />
            <div className="text-[14px] text-white/60 mb-1">You're all caught up.</div>
            <div className="text-[12px] text-white/30 max-w-[240px]">
              Friend requests, match challenges, and chain events will land here.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {staff.map((msg) => (
              <li
                key={`staff:${msg.id}`}
                className={`px-4 py-3.5 flex items-start gap-3 ${msg.readAt ? "" : "bg-[#FF6B00]/5"}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#FF6B00]/15 ring-1 ring-[#FF6B00]/30 flex items-center justify-center mt-0.5 shrink-0">
                  <Megaphone className="w-4 h-4 text-[#FF6B00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[12px] mb-0.5">
                    <span className="text-[#FF6B00] font-mono tracking-[0.06em] font-semibold">Staff message</span>
                    {!msg.readAt && <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" aria-label="unread" />}
                  </div>
                  <div className="text-[13px] text-white whitespace-pre-wrap break-words">{msg.body}</div>
                  <div className="font-mono text-[10px] text-white/30 mt-0.5">
                    {new Date(msg.createdAt).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  {!msg.readAt && (
                    <div className="mt-2.5 flex gap-2">
                      <button
                        onClick={() => friendsStore.markStaffMessageRead(msg.id)}
                        className="px-3 py-1.5 rounded-md border border-white/10 text-white/60 hover:text-white hover:border-white/30 font-mono text-[10px] tracking-[0.14em] uppercase inline-flex items-center gap-1.5"
                      >
                        <Check className="w-3 h-3" /> Mark read
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
            {invites.map((inv) => {
              const meta = GAME_BY_KEY[inv.game];
              return (
                <li key={`invite:${inv.id}`} className="px-4 py-3.5 flex items-start gap-3 bg-[#FF6B00]/5">
                  <Avatar
                    address={inv.address}
                    handle={inv.handle}
                    src={avatarUrl(inv.address, inv.avatarVersion)}
                    size={36}
                    className="ring-1 ring-white/10 mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[12px] mb-0.5">
                      <Swords className="w-3.5 h-3.5" style={{ color: meta?.accent ?? "#FF6B00" }} />
                      <span className="text-white/80 font-mono tracking-[0.06em]">Match challenge</span>
                    </div>
                    <div className="text-[13px] text-white">
                      <span className="font-semibold">{inv.handle}</span>{" "}
                      <span className="text-white/50">challenges you to</span>{" "}
                      <span className="font-semibold">{meta?.short ?? inv.game}</span>{" "}
                      <span className="text-white/50">for</span>{" "}
                      <span className="font-semibold text-emerald-400">${inv.stake}</span>
                    </div>
                    <div className="font-mono text-[10px] text-white/30 mt-0.5">
                      {shortAddr(inv.address)} ·{" "}
                      {new Date(inv.ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    {cantAfford(inv.stake) && (
                      <div className="mt-1.5 text-[11px] text-amber-500/80 flex items-center gap-1.5">
                        <Wallet className="w-3 h-3 shrink-0" />
                        Balance ${balanceUsd.toFixed(2)} — this match needs ${inv.stake}.
                      </div>
                    )}
                    <div className="mt-2.5 flex gap-2">
                      {cantAfford(inv.stake) ? (
                        <button
                          onClick={openWallet}
                          className="px-3 py-1.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8542] text-black font-mono text-[10px] tracking-[0.14em] uppercase font-bold inline-flex items-center gap-1.5"
                        >
                          <Wallet className="w-3 h-3" /> Add funds
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAcceptInvite(inv.id)}
                          className="px-3 py-1.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8542] text-black font-mono text-[10px] tracking-[0.14em] uppercase font-bold inline-flex items-center gap-1.5"
                          style={{ background: meta?.accent ?? "#FF6B00" }}
                        >
                          <Swords className="w-3 h-3" /> Accept
                        </button>
                      )}
                      <button
                        onClick={() => friendsStore.declineInvite(inv.id)}
                        className="px-3 py-1.5 rounded-md border border-white/10 text-white/60 hover:text-white hover:border-white/30 font-mono text-[10px] tracking-[0.14em] uppercase"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
            {incoming.map((req) => (
              <li key={req.id} className="px-4 py-3.5 flex items-start gap-3 bg-[#FF6B00]/5">
                <Avatar
                  address={req.fromAddress}
                  handle={req.fromHandle}
                  src={avatarUrl(req.fromAddress, req.avatarVersion)}
                  size={36}
                  className="ring-1 ring-white/10 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[12px] mb-0.5">
                    <UserPlus className="w-3.5 h-3.5 text-[#FF6B00]" />
                    <span className="text-white/80 font-mono tracking-[0.06em]">
                      Friend request
                    </span>
                  </div>
                  <div className="text-[13px] text-white">
                    <span className="font-semibold">{req.fromHandle}</span>{" "}
                    <span className="text-white/50">wants to add you</span>
                  </div>
                  <div className="font-mono text-[10px] text-white/30 mt-0.5">
                    {shortAddr(req.fromAddress)} ·{" "}
                    {new Date(req.ts).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  <div className="mt-2.5 flex gap-2">
                    <button
                      onClick={() => friendsStore.acceptIncoming(req.id)}
                      className="px-3 py-1.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8542] text-black font-mono text-[10px] tracking-[0.14em] uppercase font-bold inline-flex items-center gap-1.5"
                    >
                      <Check className="w-3 h-3" /> Accept
                    </button>
                    <button
                      onClick={() => friendsStore.declineIncoming(req.id)}
                      className="px-3 py-1.5 rounded-md border border-white/10 text-white/60 hover:text-white hover:border-white/30 font-mono text-[10px] tracking-[0.14em] uppercase"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
